import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { TrendingUp, Users, Heart, MessageCircle, Share2, BarChart3, AlertCircle, RefreshCw, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { generateInsightsStrategy } from '../lib/gemini';

interface SocialInsightsProps {
  brandId: string;
  accounts: any[];
}

export const SocialInsights: React.FC<SocialInsightsProps> = ({ brandId, accounts }) => {
  const uniqueAccounts = accounts.filter(acc => acc && acc.platform && !acc.isPlaceholder)
    .reduce((unique: any[], acc) => {
      const basePlatform = (acc.platform.includes(':') ? acc.platform.split(':')[1] : acc.platform).toLowerCase().trim();
      const existingIdx = unique.findIndex(a => {
        const existingBase = (a.platform.includes(':') ? a.platform.split(':')[1] : a.platform).toLowerCase().trim();
        return existingBase === basePlatform;
      });
      if (existingIdx === -1) {
        unique.push(acc);
      } else if (acc.selectedPageId && !unique[existingIdx].selectedPageId) {
        unique[existingIdx] = acc;
      }
      return unique;
    }, []);

  const defaultAccount = uniqueAccounts[0] || accounts[0];
  const [selectedAccount, setSelectedAccount] = useState<any>(defaultAccount);
  const platform = selectedAccount?.platform || '';
  const [insights, setInsights] = useState<any[]>([]);
  const [contentInsights, setContentInsights] = useState<any[]>([]);
  const [contentInsightsError, setContentInsightsError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [strategy, setStrategy] = useState<string | null>(null);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [days, setDays] = useState<number>(30); // Default to last 30 days



type InsightValue = {
  value: number;
  end_time?: string;
  start_time?: string;
};

type InsightMetric = {
  platform: string;
  name: string;
  title: string;
  values: InsightValue[];
  status: "returned" | "empty" | "failed" | "unavailable";
  metricKind: "time_series" | "total_value";
  chartable: boolean;
  summaryValue?: number;
  error?: string;
  restricted?: boolean;
};

function toChartDate(value: InsightValue): string {
  const rawDate = value.end_time || value.start_time;
  if (!rawDate) return "Unknown";
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toISOString().slice(0, 10);
}

function normalizeInsightMetric(metric: any): InsightMetric {
  const rawValues = Array.isArray(metric.values) ? metric.values : [];
  const values = rawValues
    .map((item: any) => {
      const numericValue =
        typeof item?.value === "number"
          ? item.value
          : typeof item?.value === "string" && item.value.trim() !== ""
            ? Number(item.value)
            : null;

      if (numericValue === null || !Number.isFinite(numericValue)) return null;

      return {
        value: numericValue,
        end_time: item.end_time,
        start_time: item.start_time,
      };
    })
    .filter(Boolean) as InsightValue[];

  return {
    platform: metric.platform,
    name: metric.name,
    title: metric.title || metric.name,
    values,
    status: metric.status || (values.length > 0 ? "returned" : "empty"),
    metricKind: metric.metricKind || "time_series",
    chartable: typeof metric.chartable === "boolean" ? metric.chartable : true,
    summaryValue: metric.summaryValue,
    error: metric.error,
    restricted: metric.restricted
  };
}

function buildChartRows(insights: InsightMetric[]) {
  const rowsByDate: Record<string, any> = {};

  for (const metric of insights) {
    if (metric.status !== "returned" || !metric.chartable || metric.metricKind !== "time_series") {
      continue;
    }

    for (const value of metric.values) {
      const date = toChartDate(value);
      if (date === "Unknown") continue;
      if (!rowsByDate[date]) {
        rowsByDate[date] = { date };
      }
      rowsByDate[date][metric.name] = value.value;
    }
  }

  return Object.values(rowsByDate).sort((a: any, b: any) =>
    a.date.localeCompare(b.date)
  );
}

  const fetchInsights = async () => {
    if (!brandId || !selectedAccount?.platform) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/social/insights", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          brandId,
          platform: selectedAccount.platform,
          accountId: selectedAccount.accountId,
          days,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to fetch insights");
      }

      const processedInsights: InsightMetric[] = (data.insights || [])
        .filter((metric: any) => metric && (metric.name || metric.title))
        .map(normalizeInsightMetric);

      setInsights(processedInsights);
      setContentInsights(data.contentInsights || []);
      setContentInsightsError(data.contentInsightsError || null);

      const chartRows = buildChartRows(processedInsights);

      setChartData(chartRows);

      const failedMetrics = processedInsights.filter(
        (metric) => metric.status === "failed",
      );

      if (failedMetrics.length > 0) {
        console.warn(
          "[Insights] Some metrics failed:",
          failedMetrics.map((metric) => ({
            name: metric.name,
            error: metric.error,
          })),
        );
      }
    } catch (error: any) {
      setError(error.message || "Unable to load insights");
      setInsights([]);
      setContentInsights([]);
      setContentInsightsError(null);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [selectedAccount, brandId, days]);

  const generateStrategy = async () => {
    setIsGeneratingStrategy(true);
    try {
      let postData = null;
      try {
        const inboxRes = await fetch('/api/social/inbox', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brandId, platform: selectedAccount.platform, accountId: selectedAccount.accountId })
        });
        if (inboxRes.ok) {
          const inboxJson = await inboxRes.json();
          // Extract just the metrics to save tokens
          postData = (inboxJson.comments || []).map((p: any) => ({
            id: p.id,
            text: p.text?.substring(0, 100) + '...',
            likes: p.metrics?.likes,
            replies: p.metrics?.replies,
            views: p.metrics?.views
          })).slice(0, 10);
        }
      } catch (e) {
        console.warn("Failed to fetch recent post data for strategy", e);
      }

      const metricsSummary = insights.map((m: any) => {
        const lastVal = m.values?.[m.values.length - 1]?.value || 0;
        return `${m.title || m.name}: ${lastVal}`;
      }).join(', ');

      const aiStrategy = await generateInsightsStrategy(metricsSummary, postData);
      setStrategy(aiStrategy);
    } catch (err: any) {
      console.error("Strategy generation failed:", err);
    } finally {
      setIsGeneratingStrategy(false);
    }
  };

  const getMetricIcon = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('impression') || n.includes('view')) return <BarChart3 className="w-4 h-4 text-blue-500" />;
    if (n.includes('reach') || n.includes('follower')) return <Users className="w-4 h-4 text-emerald-500" />;
    if (n.includes('engagement') || n.includes('like')) return <Heart className="w-4 h-4 text-pink-500" />;
    if (n.includes('comment') || n.includes('replies')) return <MessageCircle className="w-4 h-4 text-indigo-500" />;
    if (n.includes('share') || n.includes('repost')) return <Share2 className="w-4 h-4 text-orange-500" />;
    return <Zap className="w-4 h-4" />;
  };


  const cleanMetricName = (name: string, title?: string) => {
    return (title || name.replace(/_/g, ' '))
      .replace(/THREADS/gi, '')
      .replace(/page post engagements/i, 'Engagement')
      .replace(/page views total/i, 'Views')
      .replace(/page follows/i, 'Follows')
      .replace(/page video views/i, 'Video Views')
      .replace(/page actions post reactions total/i, 'Reactions')
      .replace(/page_post_engagements/i, 'Engagement')
      .replace(/page_impressions_unique/i, 'Reach')
      .replace(/page_views_total/i, 'Views')
      .replace(/page_follows/i, 'Follows')
      .replace(/page_video_views/i, 'Video Views')
      .replace(/page_actions_post_reactions_total/i, 'Reactions')
      .replace(/page_impressions/i, 'Impressions')
      .replace(/threads_profile_/i, 'Profile ')
      .replace(/_agg/i, '')
      .replace(/threads_/i, '')
      .replace(/media_/i, '')
      .replace(/DAILY TOTAL POST REACTIONS OF A PAGE/i, 'Reactions')
      .replace(/DAILY TOTAL REACH/i, 'Total Reach')
      .replace(/DAILY POST ENGAGEMENT/i, 'Engagement')
      .replace(/page_engaged_users/i, 'Engaged Users')
      .replace(/\.$/, '')
      .trim();
  };

  const [debugPerms, setDebugPerms] = useState<string[] | null>(null);
  const [showDebug, setShowDebug] = useState(false);

  const checkPermissions = async () => {
    try {
      const response = await fetch('/api/social/permissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId, platform: selectedAccount.platform, accountId: selectedAccount.accountId })
      });
      const data = await response.json();
      setDebugPerms(data.permissions || []);
      setShowDebug(true);
    } catch (err) {
      console.error("Failed to fetch debug permissions", err);
    }
  };

  const isPermissionError = error?.toLowerCase().includes('permission') || error?.toLowerCase().includes('read_insights') || error?.toLowerCase().includes('reconnect');

  const returnedInsights = insights.filter((metric) => metric.status === "returned");
  const emptyInsights = insights.filter((metric) => metric.status === "empty");
  const failedInsights = insights.filter((metric) => metric.status === "failed");
  const unavailableInsights = insights.filter((metric) => metric.status === "unavailable");

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Social Insights</h2>
          <p className="text-gray-500 font-medium">Real-time performance across your platforms.</p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 self-start lg:self-auto w-full lg:w-auto">
          {/* Custom Date Range Picker */}
          <select
            id="insights-date-range"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-full sm:w-auto bg-white text-gray-900 font-bold text-sm px-4 py-2 rounded-xl border border-gray-200 outline-none hover:border-indigo-300 focus:border-indigo-500 cursor-pointer shadow-sm transition-all h-10"
          >
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days (Default)</option>
            <option value={90}>Last 90 Days</option>
            <option value={180}>Last 180 Days</option>
          </select>

          {/* Platform filter tabs */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-2xl overflow-x-auto w-full sm:w-auto">
              {uniqueAccounts.map((acc, idx) => {
                let displayPlatform = acc.platform.includes(':') ? acc.platform.split(':')[1] : acc.platform;
                if (acc.selectedPageName) {
                   displayPlatform = `${displayPlatform} (${acc.selectedPageName})`;
                }
                const tabId = acc.accountId || acc.selectedPageId || `${acc.platform}-${idx}`;
                return (
                <button
                  key={`platform-tab-${tabId}-${idx}`}
                  onClick={() => setSelectedAccount(acc)}
                  className={`px-4 py-1.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                    selectedAccount === acc
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {displayPlatform}
                </button>
              )})}
          </div>
        </div>
      </div>

      {/* Quick Stats Grid - Better spacing and card sizing */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {insights.length > 0 ? insights.filter(metric => !(metric.name === 'instagram_new_followers' && metric.status === 'empty')).map((metric) => {
          const isCumulative = metric.name.includes('follower') || metric.name.includes('audience');
          
          function getMetricTotal(m: any) {
            if (m.status === "failed" || m.status === "unavailable" || (m.status === "empty" && m.values.length === 0 && m.summaryValue === undefined)) {
              return null;
            }
            if (m.restricted) return null;
            
            if (typeof m.summaryValue === "number") {
                return m.summaryValue;
            }

            const isCumulative = m.name.includes('follower') || m.name.includes('audience');
            if (isCumulative && m.metricKind === "time_series") {
              const values = Array.isArray(m.values) ? m.values : [];
              if (values.length === 0) return 0;
              return values[values.length - 1]?.value || 0;
            }

            if (m.name.includes("engagement_rate") && m.metricKind === "time_series") {
              const values = Array.isArray(m.values) ? m.values : [];
              if (values.length === 0) return 0;
              return values[values.length - 1]?.value || 0;
            }

            const values = Array.isArray(m.values) ? m.values : [];
            return values.reduce((sum: number, item: any) => {
              const value = typeof item.value === "number" ? item.value : Number(item.value);
              return Number.isFinite(value) ? sum + value : sum;
            }, 0);
          }

          function formatMetricDisplayValue(metric: any, total: number | null) {
            if (total === null) return "—";

            if (metric.name.includes("engagement_rate")) {
              return `${(total * 100).toFixed(2)}%`;
            }

            return total.toLocaleString();
          }

          const total = getMetricTotal(metric);

          return (
            <div key={metric.name} className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm transition-all hover:border-indigo-100 hover:shadow-md flex items-center gap-5 min-w-0">
              <div className="bg-indigo-50 p-3.5 rounded-2xl shrink-0">
                {getMetricIcon(metric.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-400 font-bold uppercase tracking-[0.1em] text-[10px] mb-1 leading-tight truncate">
                  {cleanMetricName(metric.name, metric.title)}
                </p>
                <h4 className="text-2xl font-black text-gray-900 leading-none">
                  {formatMetricDisplayValue(metric, total)}
                </h4>
              </div>
            </div>
          );
        }) : (
          <div className="col-span-full bg-white rounded-3xl p-8 border border-dashed border-gray-200 text-center text-gray-400 font-medium italic">
            Connect accounts to see quick stats
          </div>
        )}
      </div>
    {loading ? (
      <div className="bg-white rounded-3xl p-12 border border-gray-100 shadow-sm flex flex-col items-center justify-center gap-4">
        <RefreshCw className="w-10 h-10 text-indigo-600 animate-spin" />
        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Fetching Analytics...</p>
      </div>
    ) : error ? (
      <div className="bg-red-50 rounded-3xl p-8 border border-red-100 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
          <div className="flex-1">
            <p className="font-bold text-red-900">Failed to load insights</p>
            <p className="text-sm text-red-600 font-medium">{error}</p>
          </div>
        </div>
        {isPermissionError && (
          <div className="bg-white/50 p-4 rounded-2xl border border-red-200">
            <p className="text-xs text-red-800 font-medium mb-3">
              This usually happens when <strong>Insights permissions</strong> were not granted in the Facebook popup. 
              Even for Business accounts, Meta requires explicit consent.
            </p>
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={fetchInsights}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-200"
              >
                Retry Fetch
              </button>
              <button 
                onClick={() => window.location.href = window.location.pathname.includes('/brands/') ? window.location.pathname : '/brands'}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700"
              >
                Go to Brand Settings to Reconnect
              </button>
              <button 
                onClick={checkPermissions}
                className="px-4 py-2 bg-white text-gray-500 border border-gray-200 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-gray-50"
              >
                Debug Permissions
              </button>
            </div>
            
            {showDebug && debugPerms && (
              <div className="mt-4 p-4 bg-black rounded-lg text-[10px] font-mono text-green-400 overflow-x-auto">
                <p className="mb-2 text-white font-bold tracking-widest">CURRENTLY GRANTED PERMISSIONS:</p>
                <div className="flex flex-wrap gap-2">
                  {debugPerms.length > 0 ? debugPerms.map(p => (
                    <span key={p} className={`px-2 py-1 rounded ${p.includes('insights') ? 'bg-green-900 text-green-100 border border-green-500' : 'bg-gray-800 border border-gray-700'}`}>
                      {p}
                    </span>
                  )) : "NO PERMISSIONS DETECTED"}
                </div>
                {!debugPerms.includes('read_insights') && (
                  <p className="mt-2 text-red-400 font-bold">MISSING: read_insights (REQUIRED)</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    ) : (
      <>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Chart */}
          <div className="md:col-span-2 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
            <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2 uppercase tracking-tight">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Engagement Overview
            </h3>
            
            <div className="h-[300px] w-full min-h-[300px]" style={{ minWidth: 0 }}>
              {chartData.length > 0 && !insights.some(i => i.restricted) ? (
                <ResponsiveContainer width="100%" height={300} minHeight={300} debounce={200}>
                  {platform === 'Twitter' ? (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                      />
                      <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '10px' }} />
                      <Bar dataKey="likes" name="Likes" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="retweets" name="Retweets" fill="#ec4899" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  ) : (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip 
                         contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                      />
                      <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '10px' }} />
                      {insights.filter((m) => m.status === "returned" && m.chartable && m.metricKind === "time_series").map((metric, idx) => (
                        <Line 
                          key={metric.name}
                          type="monotone" 
                          dataKey={metric.name} 
                          name={cleanMetricName(metric.name, metric.title)}
                          stroke={idx === 0 ? "#6366f1" : (idx === 1 ? "#ec4899" : "#10b981")} 
                          strokeWidth={3} 
                          dot={{ r: 4 }} 
                          activeDot={{ r: 6 }} 
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center gap-4 px-6 min-h-[300px]">
                  {(platform.toLowerCase() === 'linkedin' && !selectedAccount?.selectedPageId) || insights.some(i => i.restricted) ? (
                    <>
                      <div className="bg-indigo-50 p-4 rounded-full">
                        <Users className="w-8 h-8 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-gray-900 font-bold mb-1">LinkedIn Personal Metrics</p>
                        <p className="text-gray-500 text-sm max-w-sm">
                          Your account is ready for <strong>Posting</strong>. However, LinkedIn restricts personal engagement metrics (likes/reach) to specific partner programs. 
                          These metrics are not currently available for basic developer applications.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-400 font-medium italic">
                      No historical data points available yet.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Metric Status Notices */}
            {(failedInsights.length > 0 || emptyInsights.length > 0 || unavailableInsights.length > 0) && (
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col gap-3">
                {failedInsights.length > 0 && (
                  <div className="flex items-start gap-3 bg-red-50 text-red-700 p-4 rounded-2xl text-xs font-medium">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold mb-1">Failed to fetch the following metrics:</p>
                      <ul className="list-disc list-inside opacity-90 space-y-1">
                        {failedInsights.map((m) => (
                          <li key={m.name}>
                            <span className="font-bold">{cleanMetricName(m.name, m.title)}</span>
                            {m.error ? `: ${m.error}` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                {emptyInsights.length > 0 && (
                  <div className="flex items-start gap-3 bg-gray-50 text-gray-600 p-4 rounded-2xl text-xs font-medium">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 opacity-50" />
                    <div>
                      <p className="opacity-80">
                        No data was returned for <strong>{emptyInsights.map((m) => cleanMetricName(m.name, m.title)).join(", ")}</strong> in the selected time range.
                      </p>
                    </div>
                  </div>
                )}

                {unavailableInsights.length > 0 && (
                  <div className="flex items-start gap-3 bg-orange-50 text-orange-700 p-4 rounded-2xl text-xs font-medium">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold mb-1">Unavailable:</p>
                      <ul className="list-disc list-inside opacity-90 space-y-1">
                        {unavailableInsights.map((m) => (
                          <li key={m.name}>
                            <span className="font-bold">{cleanMetricName(m.name, m.title)}</span>
                            {m.error ? `: ${m.error}` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* AI Strategy Sidebar */}
          <div className="bg-indigo-600 rounded-3xl p-8 text-white shadow-xl shadow-indigo-100 flex flex-col justify-between overflow-hidden relative">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <TrendingUp className="w-32 h-32 rotate-12" />
            </div>
            
            <div className="relative z-10">
              <h3 className="text-lg font-black uppercase tracking-tight mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                AI Strategy
              </h3>
              
              {strategy ? (
                <div className="prose prose-invert prose-sm max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                   <div dangerouslySetInnerHTML={{ __html: strategy.replace(/\n/g, '<br/>') }} className="text-sm font-medium opacity-90" />
                </div>
              ) : (
                <p className="text-indigo-100 font-medium text-sm leading-relaxed mb-6">
                  Click below to let Gemini analyze your data and suggest a winning strategy.
                </p>
              )}
            </div>

            <button
              onClick={generateStrategy}
              disabled={isGeneratingStrategy || (insights.length === 0 && platform.toLowerCase() !== 'linkedin')}
              className="relative z-10 w-full bg-white text-indigo-600 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-50 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isGeneratingStrategy ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 fill-current" />
                  {strategy ? 'Refine Strategy' : 'Generate Tips'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Top Content Table */}
        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm overflow-hidden">
          <h3 className="text-lg font-black text-gray-900 mb-6 uppercase tracking-tight">Top Content</h3>
          
          {contentInsightsError && (
             <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100 mb-6 text-sm text-amber-800 font-medium">
               {contentInsightsError}
             </div>
          )}
          
          {contentInsights && contentInsights.length > 0 ? (
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-bold uppercase tracking-wider text-xs">
                    <th className="py-4 font-black">Date</th>
                    <th className="py-4 font-black">Content</th>
                    <th className="py-4 font-black">Type</th>
                    <th className="py-4 font-black">Views / Reach</th>
                    <th className="py-4 font-black">Likes</th>
                    <th className="py-4 font-black">Comments / Replies</th>
                    <th className="py-4 font-black">Shares / Reposts</th>
                    <th className="py-4 font-black">Saves / Quotes</th>
                    <th className="py-4 font-black">Eng. Rate</th>
                    <th className="py-4 font-black">Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-gray-700 font-medium">
                  {contentInsights
                    .sort((a, b) => {
                      const getScore = (item: any) => {
                        const m = item.metrics || {};
                        const reachOrViews = m.views || m.reach || m.impressions || 0;
                        const sumEngagement = (m.likes || 0) + (m.comments || 0) + (m.replies || 0) + (m.shares || 0) + (m.reposts || 0) + (m.saves || 0) + (m.quotes || 0) + (m.reactions || 0) + (m.interactions || 0);
                        return reachOrViews * 0.4 + sumEngagement * 0.6; // weight engagement slightly higher
                      };
                      return getScore(b) - getScore(a);
                    })
                    .slice(0, 10)
                    .map((item, idx) => {
                      const m = item.metrics || {};
                      const getContentMetric = (row: any, keys: string[]) => {
                        for (const key of keys) {
                          const value = row?.metrics?.[key];
                          if (typeof value === "number" && Number.isFinite(value)) return value;
                          if (typeof value === "string" && value.trim() !== "") {
                            const parsed = Number(value);
                            if (Number.isFinite(parsed)) return parsed;
                          }
                        }
                        return null;
                      };

                      const formatContentMetric = (value: number | null) => {
                        if (value === null) return "—";
                        return value.toLocaleString();
                      };

                      const formatContentEngagementRate = (value: number | null) => {
                        if (value === null) return "—";
                        return `${(value * 100).toFixed(2)}%`;
                      };

                      const views = getContentMetric(item, ["views"]);
                      const reach = getContentMetric(item, ["reach"]);
                      const legacyImpressions = getContentMetric(item, ["legacy_impressions"]);
                      const viewsReachValue =
                        views !== null ? views :
                        reach !== null ? reach :
                        legacyImpressions !== null ? legacyImpressions :
                        null;

                      const likes = getContentMetric(item, ["likes", "reactions"]);
                      const commentsReplies = getContentMetric(item, ["comments", "replies"]);
                      const sharesReposts = getContentMetric(item, ["shares", "reposts"]);
                      const savesQuotes = getContentMetric(item, ["saves", "quotes"]);
                      const engagementRate = getContentMetric(item, ["engagement_rate"]);

                      return (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="py-4 whitespace-nowrap text-xs text-gray-500">
                            {new Date(item.created_time || item.timestamp).toLocaleDateString()}
                          </td>
                          <td className="py-4 max-w-[200px] truncate" title={item.text || item.title || ""}>
                            <div className="flex items-center gap-3">
                              {item.thumbnail_url && (
                                <img src={item.thumbnail_url} alt="thumbnail" className="w-8 h-8 rounded-lg object-cover" />
                              )}
                              <span className="truncate">{item.text || item.title || "No text"}</span>
                            </div>
                          </td>
                          <td className="py-4 whitespace-nowrap uppercase text-[10px] tracking-wider font-bold text-gray-400">
                            {item.media_type || "Post"}
                          </td>
                          <td className="py-4">
                            {formatContentMetric(viewsReachValue)}
                            {item.metricLabels?.viewsReachLabel && (
                              <span className="block text-[10px] text-gray-400 -mt-1 font-medium">{item.metricLabels.viewsReachLabel}</span>
                            )}
                          </td>
                          <td className="py-4">{formatContentMetric(likes)}</td>
                          <td className="py-4">{formatContentMetric(commentsReplies)}</td>
                          <td className="py-4">{formatContentMetric(sharesReposts)}</td>
                          <td className="py-4">{formatContentMetric(savesQuotes)}</td>
                          <td className="py-4">{formatContentEngagementRate(engagementRate)}</td>
                          <td className="py-4">
                            {item.permalink ? (
                              <a href={item.permalink} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 uppercase text-[10px] tracking-widest font-bold">
                                View
                              </a>
                            ) : "—"}
                          </td>
                        </tr>
                      );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-gray-400 font-medium italic text-center py-6">
              {platform === 'linkedin'
                ? "LinkedIn aggregate insights returned successfully. Post-level LinkedIn content insights were not returned for this period or require additional LinkedIn permissions."
                : "No content-level insights returned for this period."}
            </div>
          )}
        </div>
      </>
      )}
    </div>
  );
};
